/**
 * 外链单点登录：地址栏票据 → 换取本系统登录态
 * 本系统是hash路由，票据可能落在 # 之前也可能落在 # 之后，取参统一扫整串href
 */

// 票据参数名 → 换票action。A系统(business-pc)的 src/router/dynamic.js 把它的访问令牌以 ask 参数拼在外链URL上
export const EXTERNAL_LOGIN_RULES = [
  {
    keys: ['ask', 'ticket'],
    action: 'menu/askLoginStore',
    toPayload: (ticket) => ({ ask: ticket })
  }
]

const ALL_TICKET_KEYS = EXTERNAL_LOGIN_RULES.reduce((keys, rule) => keys.concat(rule.keys), [])
// 同标签页内已消费过的票据。票据可能是一次性的，刷新时重复换票会被后端判无效
const USED_TICKETS_KEY = 'USED_EXTERNAL_TICKETS'

const isTicketParam = (pair) => ALL_TICKET_KEYS.some((key) => pair.indexOf(`${key}=`) === 0)

function readUsedTickets() {
  try {
    return JSON.parse(sessionStorage.getItem(USED_TICKETS_KEY) || '{}') || {}
  } catch (error) {
    console.warn('读取外链票据标记失败:', error)
    return {}
  }
}

/** 票据可能被截断成非法转义串，decodeURIComponent会抛，不能让它炸掉守卫的同步段 */
function safeDecode(raw) {
  try {
    return decodeURIComponent(raw)
  } catch (error) {
    console.warn('外链票据解码失败，按无票据处理:', error)
    return null
  }
}

/**
 * 取地址栏中的外链票据
 * @returns {{name: string, ticket: string, action: string, payload: Object}|null}
 */
export function getExternalTicket() {
  for (const rule of EXTERNAL_LOGIN_RULES) {
    const matched = window.location.href.match(new RegExp(`[?&](?:${rule.keys.join('|')})=([^&#]*)`))
    if (!matched || !matched[1]) continue
    const ticket = safeDecode(matched[1])
    if (!ticket) continue
    return { name: rule.keys[0], ticket, action: rule.action, payload: rule.toPayload(ticket) }
  }
  return null
}

/** 抹掉地址栏票据参数，避免留在浏览器历史和访问日志里 */
export function clearExternalTicket() {
  const { pathname, search, hash } = window.location
  const prune = (query) => query.split('&').filter((pair) => pair && !isTicketParam(pair)).join('&')

  const cleanSearch = prune(search.replace(/^\?/, ''))
  const hashQueryIndex = hash.indexOf('?')
  let cleanHash = hash
  if (hashQueryIndex > -1) {
    const hashPath = hash.slice(0, hashQueryIndex)
    const hashQuery = prune(hash.slice(hashQueryIndex + 1))
    cleanHash = hashQuery ? `${hashPath}?${hashQuery}` : hashPath
  }

  window.history.replaceState(null, '', `${pathname}${cleanSearch ? `?${cleanSearch}` : ''}${cleanHash}`)
}

/** 剥掉路由query里的票据：to是进守卫时的快照，整包展开会把刚抹掉的ask写回地址栏 */
export function stripTicketQuery(query) {
  const rest = {}
  Object.keys(query || {}).forEach((key) => {
    if (ALL_TICKET_KEYS.indexOf(key) === -1) rest[key] = query[key]
  })
  return rest
}

/** 票据指纹，判重够用即可，也避免把令牌原文再存一份进sessionStorage */
export function ticketFingerprint(name, ticket) {
  return `${name}:${ticket.length}:${ticket.slice(-6)}`
}

export function isTicketUsed(name, ticket) {
  return readUsedTickets()[name] === ticketFingerprint(name, ticket)
}

export function markTicketUsed(name, ticket) {
  const used = readUsedTickets()
  used[name] = ticketFingerprint(name, ticket)
  try {
    sessionStorage.setItem(USED_TICKETS_KEY, JSON.stringify(used))
  } catch (error) {
    console.warn('记录外链票据失败:', error)
  }
}

/** 换票失败日志：只给状态码和msg，整包打对象会连请求体里的票据一起打出去 */
export function describeTicketFailure(error) {
  const response = error && error.response
  const status = (response && response.status) || '-'
  const body = (error && error.data) || (response && response.data)
  const text = typeof body === 'string' ? body : ((body && (body.msg || body.message)) || '')
  const msg = text || (error && error.message) || '无错误信息'
  return `status=${status} msg=${msg}`
}

/** 换票失败文案。业务失败与http异常口径不统一，兜一圈 */
export function describeTicketError(error) {
  const fallback = '外链登录失败，请重新登录'
  if (!error) return fallback
  if (typeof error === 'string') return error
  if (error.message) return error.message
  const body = error.data || (error.response && error.response.data)
  if (body) return body.msg || body.message || fallback
  return fallback
}
